// Students list (All Students for admin and counsellor, View Students for the telecaller): filters, the table with
// inline Counselor / Status / Telecaller dropdowns, row actions (Edit, Details, Block, Delete), Import and the Export menu.
import { useRef, useState } from 'react';

import { CsvPanel, useCsvExport } from 'src/front-components/lead-desk/csv-export';
import { CallQueueScreen, ContactButtons, LogCallDrawer } from 'src/front-components/lead-desk/screens-calls';
import { DetailsDrawer, EditDrawer } from 'src/front-components/lead-desk/student-drawers';
import { StatusCell } from 'src/front-components/lead-desk/screens-home';
import { Avatar, ConfirmDialog, DataTable, FilterBar, IconButton, InlineSelect, MenuButton, MultiSelect, Pager, StatusChip, UpdatedBadge, type Column } from 'src/front-components/lead-desk/kit';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { PageMasthead, ShellCard, ShellChip } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput, DeskSelect, ErrorLine } from 'src/front-components/theme/desk-controls';
import { getPalette } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import { deleteStudent, importStudents, setStudentBlocked, updateStudent, type ImportOutcome } from 'src/front-components/utils/lead-desk-data';
import {
  assignmentValue,
  DESK_STATUS_COLORS,
  DESK_STATUS_LABELS,
  EXPORT_SCOPES,
  exportRows,
  filterStudents,
  FOLLOW_UP_LABELS,
  formatCallDate,
  importTemplateCsv,
  initials,
  isLockedFor,
  isRecentlyUpdated,
  LOCKED_MESSAGE,
  NO_FILTERS,
  paginate,
  parseStudentImport,
  SEARCH_FIELDS,
  sortStudents,
  statusChangePatch,
  studentPhone,
  studentsToCsv,
  telecallerChoices,
  UNASSIGNED,
  type SearchField,
  type StudentFilters,
  type StudentRow,
} from 'src/utils/lead-desk';

const PAGE_SIZE = 25;
const statusOptions = Object.entries(DESK_STATUS_LABELS).map(([value, label]) => ({ value, label }));
const followOptions = Object.entries(FOLLOW_UP_LABELS).map(([value, label]) => ({ value, label }));

export const StudentsScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const [filters, setFilters] = useState<StudentFilters>(NO_FILTERS);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [details, setDetails] = useState<StudentRow | null>(null);
  const [editing, setEditing] = useState<StudentRow | null>(null);
  const [logging, setLogging] = useState<StudentRow | null>(null);
  const [deleting, setDeleting] = useState<StudentRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<(ImportOutcome & { rejected: number }) | null>(null);
  const [importing, setImporting] = useState<string | null>(null);
  const exporter = useCsvExport();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const counsellorLocked = ctx.profile.scope === 'OWN_COUNSELLOR';
  const telecallerLocked = ctx.profile.scope === 'OWN_TELECALLER';
  const [visibleCols, setVisibleCols] = useState<string[]>(['status', 'next', 'district', 'state']);
  const [viewMode, setViewMode] = useState<'QUEUE' | 'TABLE'>(ctx.role === 'TELECALLER' ? 'QUEUE' : 'TABLE');

  const rows = sortStudents(filterStudents(ctx.students, filters));
  const paged = paginate(rows, page, PAGE_SIZE);
  const change = (patch: Partial<StudentFilters>): void => {
    setFilters({ ...filters, ...patch });
    setPage(1);
  };
  const allOnPage = paged.items.length > 0 && paged.items.every((s) => picked.includes(s.id));

  // One save path for every inline change: write, reload, and report a failure on the page.
  const save = async (student: StudentRow, patch: Record<string, unknown>): Promise<void> => {
    setFailure(null);
    await updateStudent(student.id, patch);
    await ctx.refresh();
  };

  const block = async (student: StudentRow): Promise<void> => {
    setFailure(null);

    try {
      await setStudentBlocked(student.id, student.isBlocked !== true);
      await ctx.refresh();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not update the student.');
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleting === null) return;

    setDeleteBusy(true);
    setDeleteError(null);

    try {
      await deleteStudent(deleting.id);
      setPicked(picked.filter((id) => id !== deleting.id));
      await ctx.refresh();
      setDeleting(null);
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : 'Could not delete the student.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const onFile = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;

    setFailure(null);
    setOutcome(null);

    try {
      const parsed = parseStudentImport(await file.text(), ctx.profile.scope === 'OWN_COUNSELLOR' ? ctx.person : '');

      if (parsed.forms.length === 0) {
        setOutcome({ created: 0, failed: [], rejected: parsed.rejected.length });
        setFailure(parsed.rejected[0]?.reason ?? 'There were no students in that file.');

        return;
      }

      setImporting(`Importing 0 of ${parsed.forms.length}…`);

      const result = await importStudents(parsed.forms, (done) => setImporting(`Importing ${done} of ${parsed.forms.length}…`));

      setOutcome({ ...result, rejected: parsed.rejected.length });
      await ctx.refresh();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not read that file.');
    } finally {
      setImporting(null);

      if (fileInput.current !== null) fileInput.current.value = '';
    }
  };

  const counsellorOptions = [{ value: UNASSIGNED, label: UNASSIGNED }, ...ctx.counsellors.map((c) => ({ value: c, label: c }))];
  const telecallerOptions = [{ value: UNASSIGNED, label: UNASSIGNED }, ...ctx.telecallers.map((t) => ({ value: t, label: t }))];
  // A row's telecaller list is that student's counsellor's own team, never everyone.
  const teamFor = (s: StudentRow): Array<{ value: string; label: string }> => [{ value: UNASSIGNED, label: UNASSIGNED }, ...telecallerChoices(ctx.people, s.counsellorName).map((t) => ({ value: t, label: t }))];

  const columns: Column<StudentRow>[] = [
    {
      key: 'pick',
      header: (
        <input type="checkbox" aria-label="Select every student on this page" checked={allOnPage} onChange={() => setPicked(allOnPage ? picked.filter((id) => !paged.items.some((s) => s.id === id)) : [...new Set([...picked, ...paged.items.map((s) => s.id)])])} />
      ),
      width: '36px',
      render: (s) => <input type="checkbox" aria-label={`Select ${s.name ?? 'student'}`} checked={picked.includes(s.id)} onChange={() => setPicked(picked.includes(s.id) ? picked.filter((id) => id !== s.id) : [...picked, s.id])} />,
    },
    {
      key: 'student',
      header: 'Student Information',
      width: '260px',
      render: (s) => (
        <div style={{ display: 'flex', gap: SHELL_SPACE[2], alignItems: 'center', opacity: s.isBlocked === true ? 0.55 : 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <button type="button" onClick={() => setDetails(s)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', flexShrink: 0 }} title="View details">
            <Avatar text={initials(s.name)} size={32} />
          </button>
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text, display: 'flex', alignItems: 'center', gap: SHELL_SPACE[1], overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setDetails(s)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  font: 'inherit',
                  color: 'inherit',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
                title="Click to view student details"
              >
                <span style={{ fontWeight: 600, color: 'var(--t-color-blue, #2563eb)', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name ?? '—'}{s.isBlocked === true ? ' (blocked)' : ''}
                </span>
                {s.isPotential === true && <StatusChip label="Hot" color="red" />}
              </button>
            </div>
            <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Parent: {s.parentName ?? '—'}
            </div>
            <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.email?.primaryEmail ?? undefined}>
              {s.email?.primaryEmail ?? 'no-email@admissions.com'}
            </div>
            <div style={{ ...SHELL_TYPE.caption, color: shell.textSecondary, whiteSpace: 'nowrap' }}>
              {studentPhone(s)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'counsellor',
      header: 'Counselor',
      width: '150px',
      render: (s) =>
        ctx.profile.canReassignCounsellor && (s.counsellorName ?? '') !== '' ? (
          <InlineSelect
            scheme={ctx.scheme}
            label={`Counselor for ${s.name ?? 'student'}`}
            value={s.counsellorName ?? UNASSIGNED}
            options={counsellorOptions}
            onSave={(next) => save(s, { counsellorName: assignmentValue(next) })}
            onError={setFailure}
          />
        ) : (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {s.counsellorName ?? UNASSIGNED}
          </span>
        ),
    },
    {
      key: 'telecaller',
      header: 'Telecaller',
      width: '150px',
      render: (s) =>
        ctx.profile.canAssignTelecaller ? (
          (s.counsellorName ?? '') === '' ? (
            <span style={{ color: shell.textTertiary }} title="Assign a counsellor first">—</span>
          ) : (
            <InlineSelect
              scheme={ctx.scheme}
              label={`Telecaller for ${s.name ?? 'student'}`}
              value={s.telecallerName ?? UNASSIGNED}
              options={teamFor(s)}
              onSave={(next) => save(s, { telecallerName: assignmentValue(next) })}
              onError={setFailure}
            />
          )
        ) : (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {s.telecallerName ?? UNASSIGNED}
          </span>
        ),
    },
    ...(visibleCols.includes('status')
      ? [
          {
            key: 'status',
            header: 'Status',
            width: '140px',
            render: (s: StudentRow) =>
              ctx.profile.canEditStudent && ctx.role !== 'TELECALLER' ? (
                <InlineSelect
                  scheme={ctx.scheme}
                  label={`Status for ${s.name ?? 'student'}`}
                  value={s.deskStatus ?? 'NEW'}
                  options={statusOptions}
                  onSave={(next) => save(s, statusChangePatch(next))}
                  onError={setFailure}
                />
              ) : (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <StatusChip label={DESK_STATUS_LABELS[s.deskStatus ?? ''] ?? '—'} color={DESK_STATUS_COLORS[s.deskStatus ?? ''] ?? 'gray'} />
                </span>
              ),
          },
        ]
      : []),
    ...(visibleCols.includes('updates')
      ? [
          {
            key: 'updates',
            header: 'Follow-up & Remarks',
            width: '210px',
            render: (s: StudentRow) => (
              <div style={{ fontSize: '11px', color: 'var(--t-font-color-tertiary, #6b7280)', lineHeight: 1.3, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                {s.followUpStatus ? (
                  <span style={{ display: 'inline-block', fontWeight: 600, color: '#1d4ed8', background: 'rgba(59, 130, 246, 0.12)', padding: '1px 5px', borderRadius: 4, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                    {FOLLOW_UP_LABELS[s.followUpStatus] ?? s.followUpStatus}
                  </span>
                ) : (
                  <span style={{ color: shell.textTertiary }}>—</span>
                )}
                {s.latestRemark && (
                  <div style={{ fontWeight: 500, color: 'var(--t-font-color-secondary, #374151)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} title={s.latestRemark}>
                    "{s.latestRemark}"
                  </div>
                )}
                {(s.statusUpdatedAt || s.statusUpdatedBy) && (
                  <div style={{ fontSize: '10px', color: shell.textTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                    {s.statusUpdatedAt && <span>Updated {formatCallDate(s.statusUpdatedAt)}</span>}
                    {s.statusUpdatedBy && <span> by {s.statusUpdatedBy}</span>}
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []),
    ...(visibleCols.includes('next') ? [{ key: 'next', header: 'Next Call Date', width: '140px', render: (s: StudentRow) => <span style={{ whiteSpace: 'nowrap' }}>{formatCallDate(s.nextCallAt)}</span> }] : []),
    ...(visibleCols.includes('district') ? [{ key: 'district', header: 'District', width: '120px', render: (s: StudentRow) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{s.district ?? '—'}</span> }] : []),
    ...(visibleCols.includes('state') ? [{ key: 'state', header: 'State', width: '120px', render: (s: StudentRow) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{s.state ?? '—'}</span> }] : []),
    {
      key: 'actions',
      header: 'Actions',
      width: ctx.role === 'TELECALLER' ? '340px' : '180px',
      align: 'right',
      render: (s) => (
        <div
          style={{
            display: 'inline-flex',
            gap: '6px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {ctx.role === 'TELECALLER' && !isLockedFor(ctx.role, s) && (
            <>
              <ContactButtons student={s} callerName={ctx.person} compact onCallInitiated={() => setLogging(s)} />
              <DeskButton scheme={ctx.scheme} primary disabled={s.isBlocked === true} onClick={() => setLogging(s)}>
                Log call
              </DeskButton>
            </>
          )}
          {ctx.role === 'TELECALLER' && isLockedFor(ctx.role, s) && <span style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }} title={LOCKED_MESSAGE}>Locked</span>}
          {ctx.profile.canEditStudent && !isLockedFor(ctx.role, s) && <IconButton scheme={ctx.scheme} icon="edit" label={`Edit ${s.name ?? 'student'}`} tone="green" onClick={() => setEditing(s)} />}
          <IconButton scheme={ctx.scheme} icon="eye" label={`Details for ${s.name ?? 'student'}`} onClick={() => setDetails(s)} />
          {ctx.profile.canBlockStudent && (
            <IconButton scheme={ctx.scheme} icon="ban" tone="red" label={s.isBlocked === true ? `Unblock ${s.name ?? 'student'}` : `Block ${s.name ?? 'student'}`} onClick={() => void block(s)} />
          )}
          {ctx.profile.canDeleteStudent && <IconButton scheme={ctx.scheme} icon="trash" tone="red" label={`Delete ${s.name ?? 'student'}`} onClick={() => { setDeleteError(null); setDeleting(s); }} />}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageMasthead
        palette={getPalette(ctx.scheme)}
        scheme={ctx.scheme}
        title={ctx.role === 'TELECALLER' ? 'My Calls' : ctx.role === 'COUNSELLOR' ? 'My Students' : 'Student Workspace'}
        subtitle={`${ctx.totalStudents} records${ctx.truncated ? ` (showing the first ${ctx.students.length})` : ''}`}
        actions={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SHELL_SPACE[2], alignItems: 'center', maxWidth: '100%' }}>
            <DeskButton scheme={ctx.scheme} primary onClick={() => ctx.go('ADD')}>
              + Add Student
            </DeskButton>
            {ctx.profile.canImport && (
              <>
                <DeskButton scheme={ctx.scheme} onClick={() => exporter.run('students-import-template.csv', importTemplateCsv())}>Get Template</DeskButton>
                <DeskButton scheme={ctx.scheme} disabled={importing !== null} onClick={() => fileInput.current?.click()}>{importing ?? 'Import CSV'}</DeskButton>
                <input ref={fileInput} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(event) => void onFile(event.target.files?.[0])} />
              </>
            )}
            {ctx.profile.canExport && (
              <MenuButton
                scheme={ctx.scheme}
                label={picked.length > 0 ? `Export (${picked.length})` : 'Export'}
                icon="download"
                items={EXPORT_SCOPES.map((scope) => ({
                  id: scope.value,
                  label: scope.value === 'SELECTED' ? `${scope.label} (${picked.length})` : scope.label,
                  disabled: scope.value === 'SELECTED' && picked.length === 0,
                  onSelect: () => exporter.run(scope.file, studentsToCsv(exportRows(scope.value, rows, picked, ctx.students))),
                }))}
              />
            )}
          </div>
        }
      />

      <CsvPanel scheme={ctx.scheme} panel={exporter.panel} onClose={exporter.close} />
      <ErrorLine scheme={ctx.scheme} message={failure} />
      {outcome !== null && (
        <div style={{ ...SHELL_TYPE.body, color: shell.text, marginBottom: SHELL_SPACE[3] }}>
          <ShellChip label={`${outcome.created} imported`} color="green" />{' '}
          {outcome.rejected > 0 && <ShellChip label={`${outcome.rejected} rows skipped`} color="orange" />}{' '}
          {outcome.failed.length > 0 && <ShellChip label={`${outcome.failed.length} failed: ${outcome.failed[0].reason}`} color="red" />}
        </div>
      )}

      <ShellCard scheme={ctx.scheme} padded={false}>
        <div style={{ padding: SHELL_SPACE[3] }}>
          <FilterBar scheme={ctx.scheme}>
            {ctx.profile.canFilterByPeople ? (
              <>
                <MultiSelect
                  scheme={ctx.scheme}
                  label="Filter by Counselor"
                  width="150px"
                  options={counsellorOptions}
                  selected={filters.counsellors}
                  onChange={(counsellors) => change({ counsellors })}
                  locked={counsellorLocked ? `${ctx.person} (Me)` : undefined}
                />
                <MultiSelect scheme={ctx.scheme} label="Filter by Telecaller" width="150px" options={telecallerOptions} selected={filters.telecallers} onChange={(telecallers) => change({ telecallers })} />
              </>
            ) : (
              telecallerLocked && <MultiSelect scheme={ctx.scheme} label="Filter by Telecaller" width="150px" options={[]} selected={[]} onChange={() => undefined} locked={`${ctx.person} (Me)`} />
            )}
            <MultiSelect scheme={ctx.scheme} label="Filter by Status" width="150px" options={statusOptions} selected={filters.statuses} onChange={(statuses) => change({ statuses })} />
            <DeskSelect
              scheme={ctx.scheme}
              width="130px"
              value={filters.leadType}
              onChange={(leadType) => change({ leadType: leadType === 'HOT' || leadType === 'STANDARD' ? leadType : 'ALL' })}
              options={[{ value: 'ALL', label: 'All Leads' }, { value: 'HOT', label: 'Hot Potential' }, { value: 'STANDARD', label: 'Standard' }]}
            />
            <MultiSelect scheme={ctx.scheme} label="Filter by Followup Status" width="190px" options={followOptions} selected={filters.followUps} onChange={(followUps) => change({ followUps })} />
            <DeskSelect scheme={ctx.scheme} width="150px" value={filters.field} onChange={(field) => change({ field: field as SearchField })} options={SEARCH_FIELDS} />
            <div style={{ display: 'flex', gap: SHELL_SPACE[1], flex: '1 1 240px', minWidth: 0, maxWidth: '100%' }}>
              <DeskInput scheme={ctx.scheme} value={search} onChange={setSearch} placeholder="Search by name, mobile, email, city..." />
            <DeskButton scheme={ctx.scheme} primary onClick={() => change({ query: search })}>Search</DeskButton>
            <DeskButton scheme={ctx.scheme} title="Reset every filter" onClick={() => { setFilters(NO_FILTERS); setSearch(''); setPage(1); }}>Reset</DeskButton>
            </div>
            <MultiSelect
              scheme={ctx.scheme}
              label="Columns"
              width="130px"
              options={[
                { value: 'status', label: 'Status' },
                { value: 'updates', label: 'Follow-up & Remarks' },
                { value: 'next', label: 'Next Call Date' },
                { value: 'district', label: 'District' },
                { value: 'state', label: 'State' },
              ]}
              selected={visibleCols}
              onChange={(next) => setVisibleCols(next)}
            />
          </FilterBar>

          <DataTable scheme={ctx.scheme} columns={columns} rows={paged.items} rowKey={(s) => s.id} empty="No students match these filters." dense />
          <Pager
            scheme={ctx.scheme}
            page={paged.page}
            pages={paged.pages}
            onPage={setPage}
            summary={`${ctx.totalStudents} total students found in this workspace · showing ${paged.items.length} of ${rows.length}`}
          />
        </div>
      </ShellCard>

      {details !== null && <DetailsDrawer ctx={ctx} student={details} onClose={() => setDetails(null)} />}
      {editing !== null && <EditDrawer ctx={ctx} student={editing} onClose={() => setEditing(null)} />}
      {logging !== null && <LogCallDrawer ctx={ctx} student={logging} onClose={() => setLogging(null)} />}
      {deleting !== null && (
        <ConfirmDialog
          scheme={ctx.scheme}
          title="Delete this student?"
          message={<>This permanently removes <strong>{deleting.name ?? 'this student'}</strong> and their call history. It cannot be undone. To keep the record but stop calls, use Block instead.</>}
          confirmLabel="Delete student"
          busy={deleteBusy}
          error={deleteError}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  );
};
